import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import LogoDisplay from '@/components/LogoDisplay';
import LanguageDropdown from '@/components/LanguageDropdown';
import {
  House, CalendarCheck, Car, Users, SignOut, List, X, ChartBar, CarSimple, UserCircle, CurrencyEur, ChartLineUp, Percent, FileText, Money
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const DashboardLayout = ({ children, title }) => {
  const { user, logout } = useAuth();
  const { language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate(`/${language}`);
  };

  const getNavLinks = () => {
    const p = `/${language}`;
    switch (user?.role) {
      case 'admin':
        return [
          { name: 'Dashboard', path: `${p}/admin`, icon: ChartBar },
          { name: 'Réservations', path: `${p}/admin/bookings`, icon: CalendarCheck },
          { name: 'Chauffeurs', path: `${p}/admin/drivers`, icon: CarSimple },
          { name: 'Clients', path: `${p}/admin/clients`, icon: Users },
          { name: 'Flotte Admin', path: `${p}/admin/fleet`, icon: Car },
          { name: 'Tarifs', path: `${p}/admin/pricing`, icon: CurrencyEur },
          { name: 'Finance', path: `${p}/admin/financial`, icon: ChartLineUp },
          { name: 'Commissions', path: `${p}/admin/commissions`, icon: Percent },
          { name: 'Documents', path: `${p}/admin/documents`, icon: FileText },
        ];
      case 'driver':
        return [
          { name: 'Mes Courses', path: `${p}/driver`, icon: Car },
          { name: 'Mes Gains', path: `${p}/driver/earnings`, icon: Money },
          { name: 'Mes Factures', path: `${p}/driver/invoices`, icon: FileText },
        ];
      case 'client':
      default:
        return [
          { name: 'Dashboard', path: `${p}/client`, icon: House },
          { name: 'Mes Réservations', path: `${p}/client/bookings`, icon: CalendarCheck },
          { name: 'Nouvelle Course', path: `${p}/client/new-booking`, icon: Car },
        ];
    }
  };

  const navLinks = getNavLinks();

  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  return (
    <div className="flex min-h-screen overflow-x-hidden bg-[#0A0A0A]" data-testid="dashboard-layout">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-[min(18rem,calc(100vw-1rem))] overflow-y-auto border-r border-white/10 bg-[#141414] transform transition-transform duration-300 lg:w-64 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <Link to={`/${language}`}>
              <div className="inline-flex rounded-2xl border border-[#D4AF37]/15 bg-[#101010] px-3 py-3">
                <LogoDisplay className="h-[2.625rem] w-[10.625rem]" priority />
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-2">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 ${
                    isActive
                      ? 'bg-[#D4AF37] text-[#0A0A0A]'
                      : 'text-[#A1A1AA] hover:bg-white/5 hover:text-white'
                  }`}
                  data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  <link.icon size={22} weight={isActive ? 'fill' : 'regular'} />
                  <span className="font-medium">{link.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* User & Logout */}
          <div className="p-4 border-t border-white/10 flex-shrink-0">
            <div className="flex items-center gap-3 px-4 py-3 mb-2">
              <UserCircle size={32} className="text-[#D4AF37]" />
              <div>
                <p className="font-medium text-white">{user?.name}</p>
                <p className="text-xs text-[#A1A1AA] capitalize">{user?.role}</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full border-white/10 text-[#A1A1AA] hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/50"
              data-testid="logout-btn"
            >
              <SignOut size={18} className="mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="min-w-0 flex-1 overflow-x-hidden lg:ml-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 border-b border-white/10 bg-[#0A0A0A]/80 backdrop-blur-lg">
          <div className="app-shell flex items-center justify-between gap-3 py-4">
            <div className="flex min-w-0 items-center gap-3 sm:gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/10 text-white transition-colors hover:text-[#D4AF37] lg:hidden"
                data-testid="mobile-menu-btn"
                aria-label={sidebarOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              >
                {sidebarOpen ? <X size={24} /> : <List size={24} />}
              </button>
              <h1 className="min-w-0 break-words text-lg font-bold font-['Cormorant_Garamond'] sm:text-xl md:text-2xl">{title}</h1>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={handleLogout}
                variant="outline"
                className="lg:hidden border-white/10 text-[#A1A1AA] hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/50"
                data-testid="mobile-logout-btn"
              >
                <SignOut size={16} />
              </Button>
              <LanguageDropdown />
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="min-w-0 overflow-x-hidden py-4 sm:py-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="app-shell"
          >
            {children}
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
